import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { FormControl } from '@angular/forms';

import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { KENDO_FORM } from '@progress/kendo-angular-inputs';

import { GridColumn, GridValidationErrorMessage } from '@nexweb/shared/ui/column';
import { isNullOrUndefined, replaceErrorTags } from '@nexweb/util';

@Component({
  selector: 'nexweb-error-messages',
  templateUrl: './error-messages.component.html',
  imports: [KENDO_FORM, LocalizationPipe],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ErrorMessagesComponent {
  @Input() control: FormControl;
  @Input() column: GridColumn;

  private localizationService = inject(LocalizationService);

  public get errorMessage() {
    for (const propertyName in this.control?.errors) {
      if (
        // eslint-disable-next-line no-prototype-builtins
        this.control.errors.hasOwnProperty(propertyName) ||
        this.control.touched
      ) {
        if (!isNullOrUndefined(this.column.validations?.errorMessages)) {
          const errorMessages = this.column.validations?.errorMessages;
          const errorMessage: GridValidationErrorMessage = errorMessages.find((x) => x.name === propertyName);

          if (errorMessage !== undefined) {
            const errorValue = this.control.errors[propertyName];

            if (errorValue && typeof errorValue === 'object') {
              const localizedMessage = this.localizationService.instant(errorMessage.message);

              return replaceErrorTags(localizedMessage, errorValue);
            }

            return errorMessage.message;
          } else {
            const validatorErrorMessage = this.getValidatorErrorMessage(
              propertyName,
              this.control.errors[propertyName],
            );

            const errors = this.control[`errors`] as unknown;

            return validatorErrorMessage
              ? validatorErrorMessage
              : errors[`message`]
                ? errors[`message`]
                : errors[`errorMessage`]
                  ? errors[`errorMessage`]
                  : '';
          }
        } else if (this.column.validations?.errorMessage) {
          return this.column.validations.errorMessage;
        } else {
          const validatorErrorMessage = this.getValidatorErrorMessage(propertyName, this.control.errors[propertyName]);

          const errors = this.control[`errors`] as unknown;

          return validatorErrorMessage
            ? validatorErrorMessage
            : errors[`message`]
              ? errors[`message`]
              : errors[`errorMessage`]
                ? errors[`errorMessage`]
                : '';
        }
      }
    }

    return null;
  }

  public get warningMessage() {
    if (this.column.validations) {
      if (this.column.validations?.warningMessages) {
        const warningMessages = this.column.validations?.warningMessages;

        if (!isNullOrUndefined(warningMessages)) {
          if (warningMessages.length > 0) {
            let warningMessage = '';

            for (const item of warningMessages) {
              if (warningMessages.length === 1) {
                warningMessage += item.message;
              } else {
                warningMessage += item.message + ', ';
              }
            }

            return warningMessage;
          }
        }
      }
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getValidatorErrorMessage(validatorName: string, validatorValue?: any) {
    const messages = {
      required: 'AbpValidation::ThisFieldIsRequired.',
      invalidCreditCard: 'AbpValidation::ThisFieldIsNotAValidCreditCardNumber.',
      email: 'AbpValidation::ThisFieldIsNotAValidEmailAddress.',
      invalidPhoneNumber: 'AbpValidation::ThisFieldIsNotAValidPhoneNumber.',
      invalidPasswordLength: this.localizationService.instant(
        {
          key: 'AbpIdentity::Description:Abp.Identity.Password.RequiredLength',
          defaultValue: '',
        },
        validatorValue.requiredLength,
      ),
      invalidPassword1Number: 'AbpIdentity::DisplayName:Abp.Identity.Password.RequireDigit',
      invalidPassword1Capital: 'AbpIdentity::DisplayName:Abp.Identity.Password.RequireUppercase',
      invalidPassword1Lower: 'AbpIdentity::DisplayName:Abp.Identity.Password.RequireLowercase',
      invalidPassword1Special: 'AbpIdentity::DisplayName:Abp.Identity.Password.RequireNonAlphanumeric',
      invalidPasswordContainsFirstName: 'Aum::Aum:PasswordContainsFirstName',
      invalidPasswordContainsLastName: 'Aum::Aum:PasswordContainsLastName',
      invalidPasswordContainsUsername: 'Aum::Aum:PasswordContainsUsername',
      dateLessThanToday: 'Aum::ValidationMessage:DateLessThanToday',
      validFromToDatesOverlap: 'NexBase::NexBase:ValidFromToDatesOverlap',
      dateLessThanValidDate: 'Aum::ValidationMessage:DateLessThanValidDate',
      provideDeactivationReason: 'Aum::ValidationMessage:ProvideDeactivationReason',
      provideDeactivationDate: 'Aum::ValidationMessage:ProvideDeactivationDate',
      sammExistingDate: 'Samm::ValidationMessage:ExistingDate',
      queryExistingDate: 'NexBase::ValidationMessage:ExistingQueryDate',
      settlementExistingDate: 'NexBase::ValidationMessage:ExistingSettlementDate',
      fromMustBeLessThanToDate: 'NexBase::NexBase:FromMustBeLessThanToDate',
      toMustBeGreaterThanFromDate: 'NexBase::NexBase:ToMustBeGreaterThanFromDate',
      lessThanMinimumDate: 'NexBase::NexBase:LessThanMinimumDate',
      greaterThanMaximumDate: 'NexBase::NexBase:GreaterThanMaximumDate',
      lessThanMinimum: 'NexBase::NexBase:LessThanMinimum',
      greaterThanMaximum: 'NexBase::NexBase:GreaterThanMaximum',
      maxError: 'NexBase::NexBase:GreaterThanMaximum',
      alreadyExists: 'NexBase::NexBase:AlreadyExists',
      startDateGreaterThanEndDate: 'NexBase::ValidationMessage:StartDateGreaterThanEndDateError',
      endDateLessThanStartDate: 'NexBase::NexBase:ToMustBeGreaterThanFromDate',
    };

    return messages[validatorName];
  }
}
